import { FunctionComponent, memo, useMemo } from "react";
import { Box, BoxContent, BoxHeader, BoxHeading, NumericValue } from "@edgetier/client-components";
import { IInteractionsTaggedChartData, IProps } from "./phrase-triggers-performance-chart.types";
import PhraseTriggersPerformanceChartFilters from "./phrase-triggers-performance-chart-filters";
import { TimePeriodGroupBy } from "@edgetier/watchtower-types";
import useInteractionsCounts from "hooks-for/interactions/use-interactions-counts";
import MaximumChartData from "components-for/charts/maximum-chart-data";
import { Bar, BarChart, CartesianGrid, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button, SpinnerUntil } from "@edgetier/components";
import { formatPerformanceChartData } from "./phrase-triggers-performance-chart.utilities";
import PhraseCountsChartTooltip from "components-for/anomalies/phrase-counts-chart/phrase-counts-chart-tooltip";
import { dateTimeUpperXAxisTickFormatter } from "utilities-for/charts/date-time-upper-x-axis-tick-formatter";
import MetricValuesChartDateRange from "pages/anomaly/anomaly-metric-values-chart/metric-values-chart-date-range";
import { faUndo } from "@fortawesome/pro-solid-svg-icons";
import { PHRASE_TRIGGER_GROUP } from "./phrase-triggers-performance-chart.constants";
import "./phrase-triggers-performance-chart.scss";

/**
 * A chart showing number of triggers per day
 * @param props.filters         Current selected filters (date time range)
 * @param props.updateFilters   Callback to update said filters
 * @param phraseTriggerId       Current phrase trigger id
 * @param props.width
 * @param props.height
 */
const PhraseTriggersPerformanceChart: FunctionComponent<IProps> = ({
    filters,
    updateFilters,
    width = "100%",
    height = "100%",
    phraseTriggerId,
    mouseCallbacks,
    chartFilterRange,
    resetChartFilterRange,
    tagId,
}) => {
    // Get counts of phrases or interactions according to the parameters.
    const { data: queryChartData, isLoading: isQueryChartDataLoading } = useInteractionsCounts(
        {
            ...filters,
            groupBy: [TimePeriodGroupBy.Days, PHRASE_TRIGGER_GROUP],
            tagId: [tagId],
        },
        { select: (counts) => counts.filter(() => true) }
    );

    // Format chart data for either day or trigger filters
    const chartData = useMemo(
        () => formatPerformanceChartData(phraseTriggerId, queryChartData),
        [phraseTriggerId, queryChartData]
    );

    // Compute total interactions tagged by trigger.
    const totalInteractionsTaggedByTrigger = useMemo(
        () => chartData.reduce((sum, triggerData) => (sum += triggerData.count), 0),
        [chartData]
    );

    return (
        <Box className="phrase-triggers-performance-chart">
            <BoxHeader>
                <BoxHeading>
                    <span>Interactions Tagged by Trigger </span>
                    {!isQueryChartDataLoading && (
                        <>
                            (<NumericValue value={totalInteractionsTaggedByTrigger} />)
                        </>
                    )}
                </BoxHeading>
                <PhraseTriggersPerformanceChartFilters initialValues={filters} onSubmit={updateFilters} />
            </BoxHeader>

            <BoxContent>
                <div className="anomaly-metric-values-charts__filter-info">
                    <MetricValuesChartDateRange
                        firstDate={chartFilterRange[0].date}
                        secondDate={chartFilterRange[1].date}
                    />
                    <Button
                        disabled={chartFilterRange.some(({ activeLabel }) => activeLabel === null)}
                        icon={faUndo}
                        onClick={() => resetChartFilterRange()}
                        isIconOnly
                    />
                </div>
                <br />
                <div className="phrase-triggers-performance-chart__container">
                    <SpinnerUntil<IInteractionsTaggedChartData[]> data={chartData} isReady={!isQueryChartDataLoading}>
                        {(data) => (
                            <MaximumChartData dataLength={data.length}>
                                <ResponsiveContainer width={width} height={height}>
                                    <BarChart data={data} {...mouseCallbacks}>
                                        <CartesianGrid />
                                        <XAxis
                                            dataKey="dateTime"
                                            tickFormatter={(tickValue) => {
                                                return dateTimeUpperXAxisTickFormatter(
                                                    filters.startDateTime,
                                                    tickValue,
                                                    TimePeriodGroupBy.Days
                                                );
                                            }}
                                        />
                                        <YAxis yAxisId={0} tickFormatter={(value) => value} />
                                        <Tooltip
                                            content={({ active, payload }) =>
                                                active &&
                                                typeof payload !== "undefined" &&
                                                Array.isArray(payload) &&
                                                payload.length > 0 && (
                                                    <PhraseCountsChartTooltip
                                                        startDateTime={filters.startDateTime}
                                                        endDateTime={filters.endDateTime}
                                                        dateTime={payload[0].payload.dateTime}
                                                        proportion={false}
                                                        timePeriodGroup={TimePeriodGroupBy.Days}
                                                        value={payload[0].payload.count}
                                                    />
                                                )
                                            }
                                        />
                                        <Bar dataKey="count" radius={5} fill={"#3498db"} />
                                        <ReferenceArea
                                            fill={"#1abc9c"}
                                            fillOpacity={0.3}
                                            x1={chartFilterRange?.[0].activeLabel ?? ""}
                                            x2={chartFilterRange?.[1].activeLabel ?? ""}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </MaximumChartData>
                        )}
                    </SpinnerUntil>
                </div>
            </BoxContent>
        </Box>
    );
};

export default memo(PhraseTriggersPerformanceChart);
